import { useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import '../LegalPage.css';

export type Lang = 'ja' | 'en' | 'zh';

export type LegalSection = {
  title: string;
  body?: string[];
  list?: string[];
  after?: string[];
};

export type LegalDoc = {
  eyebrow: string;
  title: string;
  lead: string;
  sections: LegalSection[];
  dates: string[];
};

export function useLang(): Lang {
  const { i18n } = useTranslation();
  const l = (i18n.resolvedLanguage || i18n.language || 'ja').slice(0, 2);
  return (['ja', 'en', 'zh'].includes(l) ? l : 'ja') as Lang;
}

export function LegalHero({ eyebrow, title, lead }: { eyebrow: string; title: string; lead: string }) {
  useEffect(() => {
    document.title = `${title} | Stay Dream Hotel`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <section className="legal-hero">
      <div className="legal-hero-inner">
        <p className="legal-eyebrow">{eyebrow}</p>
        <h1 className="legal-title">{title}</h1>
        <p className="legal-lead">{lead}</p>
      </div>
    </section>
  );
}

export function LegalDocument({ doc, children }: { doc: LegalDoc; children?: ReactNode }) {
  return (
    <div className="legal-page">
      <LegalHero eyebrow={doc.eyebrow} title={doc.title} lead={doc.lead} />
      <section className="legal-body">
        <div className="legal-body-inner">
          {doc.sections.map((s, i) => (
            <div className="legal-section" key={i}>
              <h2>{`${i + 1}. ${s.title}`}</h2>
              <div className="gold-line" />
              {s.body?.map((p, j) => <p key={j}>{p}</p>)}
              {s.list && (
                <ul>
                  {s.list.map((li, j) => <li key={j}>{li}</li>)}
                </ul>
              )}
              {s.after?.map((p, j) => <p key={j}>{p}</p>)}
            </div>
          ))}
          {children}
          <div className="legal-dates">
            {doc.dates.map((d, i) => <p key={i}>{d}</p>)}
          </div>
        </div>
      </section>
    </div>
  );
}

export const CONTACT_EMAIL = 'info@staydreamhotel.com';
